import { memo, useEffect, useMemo, useState } from "react";
import { Toaster } from "react-hot-toast";
import BackButtonWrapper from "../../components/button/BackButton/wrapper";
import { useTranslation } from "react-i18next";
import MusterStationTable from "../../components/table/MusterStationTable";
import MusterStationCountsCard from "../../components/card/MusterStationCountsCard";
import {
  useGetEvacuationEventsParticipantsCounts,
  useMusterPointsByUser,
} from "../../hooks/emergency-evacuation/query";
import { EVACUATION_EVENT_PARTICIPANTS_STATUSES } from "../../constants/common";
import { mapToOptions } from "../../lib/functions";
import { Controller, useForm } from "react-hook-form";
import ComboboxInput from "../../components/inputs/ComboboxInput";

const MusterStation = () => {
  const { t } = useTranslation();

  const [selectedStatus, setSelectedStatus] = useState<number>(
    EVACUATION_EVENT_PARTICIPANTS_STATUSES.MISSING,
  );
  const [selectedMusterPoint, setSelectedMusterPoint] = useState<number>(0);

  const { control, setValue } = useForm();

  const { data: allMusterPointsByUser } = useMusterPointsByUser();

  const {
    data: evacuationEventsParticipantsCounts,
    refetch: refetchEvacuationEventsParticipantsCounts,
  } = useGetEvacuationEventsParticipantsCounts({
    s_MusterPointIDs: selectedMusterPoint
      ? selectedMusterPoint?.toString()
      : "",
    s_EvacuationStatus: selectedStatus ? selectedStatus?.toString() : "",
  });

  const countsCardsData = useMemo(
    () => [
      {
        id: 1,
        title: t("total"),
        count: evacuationEventsParticipantsCounts?.expected || 0,
        bgColor: "bg-[#ffa31a]",
      },
      {
        id: 1,
        title: t("missing"),
        count: evacuationEventsParticipantsCounts?.missing || 0,
        bgColor: "bg-[#ff4d4d]",
      },
      {
        id: 2,
        title: t("safeAndExited"),
        count:
          evacuationEventsParticipantsCounts?.safe +
            evacuationEventsParticipantsCounts?.exited || 0,
        bgColor: "bg-[#47d147]",
      },
    ],
    [
      evacuationEventsParticipantsCounts?.exited,
      evacuationEventsParticipantsCounts?.expected,
      evacuationEventsParticipantsCounts?.missing,
      evacuationEventsParticipantsCounts?.safe,
      t,
    ],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      refetchEvacuationEventsParticipantsCounts();
    }, 5000);

    return () => clearInterval(interval);
  }, [refetchEvacuationEventsParticipantsCounts]);

  const musterPoints = useMemo(
    () =>
      mapToOptions(
        "s_MusterPointName_En",
        "s_MusterPointName_Ar",
        "n_MusterPointID",
        allMusterPointsByUser,
      ),
    [allMusterPointsByUser],
  );

  useEffect(() => {
    if (allMusterPointsByUser?.length === 1) {
      setValue("musterPoint", allMusterPointsByUser?.[0]?.n_MusterPointID);
    }
  }, [allMusterPointsByUser, setValue]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("musterStation")}
          </h1>
        </BackButtonWrapper>

        <Controller
          name="musterPoint"
          control={control}
          render={({ field: { onChange, value } }) => (
            <ComboboxInput
              options={musterPoints}
              placeholder={t("selectMusterPoint")}
              value={
                musterPoints?.find((item) => item.value === value)?.labelEn ||
                ""
              }
              onChange={(value) => {
                onChange(value);
                setSelectedMusterPoint(Number(value));
              }}
              styles="-mt-2 md:-mt-6 sm:w-[60%] md:w-1/2 lg:w-[40%] xl:w-[30%] py-2 rounded-lg ring-1 ring-purple-primary dark:ring-white focus:outline-none focus:ring-1"
              valueStyles="dark:text-white"
              chevronsStyles="dark:text-white"
            />
          )}
        />

        <div className="flex items-center gap-2 -my-2 md:-my-5 lg:-mt-5 lg:-mb-6">
          {countsCardsData.map((item) => (
            <MusterStationCountsCard key={item.id} data={item} />
          ))}
        </div>

        <MusterStationTable
          setSelectedStatus={setSelectedStatus}
          selectedMusterPoint={selectedMusterPoint}
        />
      </div>
    </>
  );
};

export default memo(MusterStation);
